import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

interface AddBonusDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    week_start_date: string;
    bonus_date?: string;
    amount: number;
    description?: string;
  }) => Promise<void>;
  workerId: number;
  firstDayOfWeek?: string;
}

export const AddBonusDialog: React.FC<AddBonusDialogProps> = ({ 
  open, 
  onClose, 
  onSave, 
  workerId,
  firstDayOfWeek = 'Monday'
}) => {
  const [weekStartDate, setWeekStartDate] = useState<Dayjs | null>(dayjs().startOf('week'));
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Calcular el inicio de semana según la configuración de la empresa
  const getWeekStart = (date: Dayjs): Dayjs => {
    const dayMap: { [key: string]: number } = {
      'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
      'Thursday': 4, 'Friday': 5, 'Saturday': 6
    };
    const targetDay = dayMap[firstDayOfWeek] ?? 1;
    let current = date.clone();
    while (current.day() !== targetDay) {
      current = current.subtract(1, 'day');
    }
    return current;
  };

  const handleSave = async () => {
    try {
      setError(null);

      if (!weekStartDate) {
        setError('La fecha de inicio de semana es requerida');
        return;
      }

      if (!amount || parseFloat(amount) <= 0) {
        setError('El monto debe ser mayor a 0');
        return;
      }

      const weekStart = getWeekStart(weekStartDate);

      const data = {
        week_start_date: weekStart.format('YYYY-MM-DD'),
        bonus_date: weekStartDate.format('YYYY-MM-DD'),
        amount: parseFloat(amount),
        description: description || undefined,
      };

      console.log('📅 Agregando bono con datos:', {
        fecha_seleccionada: weekStartDate.format('YYYY-MM-DD'),
        week_start_date_calculado: data.week_start_date,
        amount: data.amount,
        firstDayOfWeek: firstDayOfWeek
      });

      setSaving(true);
      await onSave(data);
      
      // Resetear formulario
      setWeekStartDate(dayjs().startOf('week'));
      setAmount('');
      setDescription('');
      setError(null);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al guardar el bono');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setWeekStartDate(dayjs().startOf('week'));
    setAmount('');
    setDescription('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar Bono Semanal</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Fecha (se calculará inicio de semana automáticamente) *"
              value={weekStartDate}
              onChange={(newValue) => setWeekStartDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  helperText: `Inicio de semana: ${weekStartDate ? getWeekStart(weekStartDate).format('DD/MM/YYYY') : ''}`,
                },
              }}
            />
          </LocalizationProvider>

          <TextField
            label="Monto del Bono (US$) *"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            required
            inputProps={{ min: 0, step: 0.01 }}
          />

          <TextField
            label="Descripción (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Ej: Bono por productividad, Bono por asistencia perfecta, etc."
          />

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            El bono se sumará al total de pago de la semana seleccionada.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving} color="success">
          {saving ? 'Guardando...' : 'Agregar Bono'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
